import React from 'react';
import { useIntl } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import type { EditorActions } from '@atlaskit/editor-core';

import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import { EmojiTitleStateContainer } from '@confluence/emoji-title/entry-points/emojiTitleStateContainer';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { CoverPictureStateContainer } from '@confluence/page-cover-picture';
import { fg } from '@confluence/feature-gating';

import { SaveIndicatorComponent } from './SaveIndicatorComponent';
import { SaveIndicatorLivePages } from './SaveIndicatorLivePages';

export type SaveIndicatorProps = {
	editorActions: EditorActions | undefined;
	saveError: boolean;
	title: string | null;
};

const SaveIndicatorWrapper = ({ editorActions, saveError, title }: SaveIndicatorProps) => {
	const { formatMessage } = useIntl();
	const isLivePage = useIsCurrentPageLive();

	return (
		<Subscribe to={[EmojiTitleStateContainer, CoverPictureStateContainer]}>
			{(
				emojiTitleState: EmojiTitleStateContainer,
				coverPictureState: CoverPictureStateContainer,
			) => {
				const selectingEmojiTitle = emojiTitleState.state.isUpdatingEmoji;
				const selectingCoverPicture =
					coverPictureState.state.isUploadingPicture ||
					coverPictureState.state.isRemovingPicture ||
					coverPictureState.state.isRepositioningPicture;
				const hasEmoji = !!emojiTitleState.state.selectedEmoji;
				const hasCoverPicture = coverPictureState.state.hasCoverPicture;
				return isLivePage && fg('live_pages_save_indicator') ? (
					<SaveIndicatorLivePages
						editorActions={editorActions}
						saveError={saveError}
						title={title}
						hasEmoji={hasEmoji}
						hasCoverPicture={hasCoverPicture}
						selectingEmojiTitle={selectingEmojiTitle}
						selectingCoverPicture={selectingCoverPicture}
					/>
				) : (
					<SaveIndicatorComponent
						editorActions={editorActions}
						saveError={saveError}
						title={title}
						hasEmoji={hasEmoji}
						hasCoverPicture={hasCoverPicture}
						selectingEmojiTitle={selectingEmojiTitle}
						selectingCoverPicture={selectingCoverPicture}
						formatMessage={formatMessage}
					/>
				);
			}}
		</Subscribe>
	);
};

export const SaveIndicator = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_MODES,
})(SaveIndicatorWrapper);
